/** Display-only dedup for the preview modal's advisory notices list.
 *  Identical causes pushed once per leg/item collapse into one row; the
 *  affected step refs are kept ("steps 3, 7") so nothing is silently
 *  dropped. BLOCKING reasons (blocked-pass, inconsistent, hard-stop,
 *  cannot-couple, fatal) never collapse — every one renders verbatim.
 *  `staleReasons` itself is untouched. */
export interface NoticeGroup {
  body: string
  refs: string[]   // "step N" refs pulled off step-prefixed notices
  count: number    // total merged rows — renders as ×N when there are no refs
  blocking: boolean
}

const BLOCKING_NOTICE =
  /blocked-pass:|inconsistent|hard-stop|cannot couple|no animatable steps/i

/** `advisoryOnly`: warnings carry WARN severity by contract — they always
 *  render amber even when the text accidentally matches a blocking word. */
export const collapseStaleNotices = (
  raw: string[],
  opts: { advisoryOnly?: boolean } = {}
): NoticeGroup[] => {
  const groups = new Map<string, NoticeGroup>()
  const order: NoticeGroup[] = []
  for (const r of raw) {
    if (!opts.advisoryOnly && BLOCKING_NOTICE.test(r)) {
      order.push({ body: r, refs: [], count: 1, blocking: true })
      continue
    }
    const stepRef = r.match(/^(step \d+):\s*/)
    const body = stepRef ? r.slice(stepRef[0].length) : r
    const ref = stepRef ? stepRef[1] : null
    const existing = groups.get(body)
    if (existing) {
      existing.count++
      if (ref && !existing.refs.includes(ref)) existing.refs.push(ref)
      continue
    }
    const g: NoticeGroup = { body, refs: ref ? [ref] : [], count: 1, blocking: false }
    groups.set(body, g)
    order.push(g)
  }
  return order
}

/** Backend `item.warnings[]` (WARN-severity reasons) → operator-facing
 *  advisory text. UNSEATED_ARRIVAL warnings already name the cars; the
 *  reword adds the approximation disclosure the operator needs. Unknown
 *  warnings fall through verbatim. */
export const describeWarning = (raw: string): string => {
  const m = raw.match(
    /^Cut on (.+?) names manifest car\(s\) not yet seated \(unseated arrivals\): (.+?) — counted as virtual occupants/
  )
  if (m) {
    // Backend marks arrive as a Java list literal — strip the brackets and
    // render as a plain-English list ("A, B and C").
    const marks = m[2]
      .replace(/[\[\]]/g, '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    const list =
      marks.length > 1
        ? `${marks.slice(0, -1).join(', ')} and ${marks[marks.length - 1]}`
        : marks.join('')
    return (
      `${list} ${marks.length === 1 ? 'is' : 'are'} not yet seated on ${m[1]}. ` +
      `Shown approximately at the switch end of ${m[1]} until the ` +
      `${marks.length === 1 ? 'car is' : 'cars are'} assigned a track on receipt.`
    )
  }
  return raw
}
